import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ScrollableContent from '@wix/da-ds/pkg/ScrollableContent';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import DSModal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import { fetchGifs } from '../../../redux/actions/extension-gif';
import {
  gifItems,
  gifPage,
  gifFetching,
  gifHasMore,
  gifApiError,
} from '../../../redux/selectors/extension-gif';
import { GifObject } from './types';
import PoweredByGiphy from './PoweredByGiphy';

import s from './GiphyPopup.scss';

interface Props {
  onSelect: (gif: GifObject) => void;
  onClose: () => void;
}

const FETCH_TRIGGER_DISTANCE = 80;

const GiphyModal: React.FC<Props> = ({ onSelect, onClose }) => {
  const { t } = useTranslation('editor');
  const [searchTag, setSearchTag] = useState<string>('');
  const onChange = useCallback(
    e => {
      setSearchTag(e.target.value);
    },
    [setSearchTag]
  );

  const dispatch = useDispatch();

  const gifs = useSelector(gifItems);
  const page = useSelector(gifPage);
  const fetching = useSelector(gifFetching);
  const hasMore = useSelector(gifHasMore);
  const failed = useSelector(gifApiError);

  const onScroll = useCallback(
    (_, distanceToBottom) => {
      if (
        !fetching &&
        !failed &&
        hasMore &&
        distanceToBottom < FETCH_TRIGGER_DISTANCE
      ) {
        dispatch(fetchGifs(searchTag, page));
      }
    },
    [dispatch, searchTag, page, fetching, failed, hasMore]
  );

  const onClick = useCallback(
    gif => {
      onSelect({
        originalUrl: gif.images.original.url,
        originalMp4: gif.images.original.mp4,
        stillUrl: gif.images.original_still.url,
        downsizedUrl: gif.images.downsized.url,
        downsizedStillUrl: gif.images.downsized_still.url,
        downsizedSmallMp4: gif.images.downsized_small.mp4,
        height: parseInt(gif.images.original.height),
        width: parseInt(gif.images.original.width),
      });
      onClose();
    },
    [onSelect, onClose]
  );

  useEffect(() => {
    dispatch(fetchGifs(searchTag, 0));
  }, [dispatch, searchTag]);

  const input = React.createRef<HTMLInputElement>();

  return (
    <DSModal
      useFixedMobileHeight
      isOpen={true}
      hasCloseButton
      onRequestClose={onClose}
      heading={t('ext.gif.search.title')}
      preamble={
        <TextInput
          ref={input}
          id="giphyUploadModalInput"
          label=""
          placeholder={t('ext.gif.search.placeholder')}
          onChange={onChange}
          value={searchTag}
          data-hook="giphyUploadModalInput"
          autoFocus
        />
      }
      footerRight={[<PoweredByGiphy key="powered-by-giphy" />]}
    >
      <div className={s['infinite_scroll_container']}>
        {!(gifs || []).length && searchTag ? (
          <div className={s['empty_state']}>
            <div className={s['empty_title']}>
              {t('ext.gif.search.noResults.title')}
            </div>
            <div className={s['empty_subtitle']}>
              {t('ext.gif.search.noResults.text')}
            </div>
          </div>
        ) : (
          <ScrollableContent
            onScrolled={onScroll}
            className={s['infinite_scroll']}
          >
            {(gifs || []).map((gif, i) => {
              return (
                <div
                  key={i}
                  role="button"
                  tabIndex={0}
                  className={s['gif_img_container']}
                  onClick={() => onClick(gif)}
                >
                  <img
                    loading="lazy"
                    className={s['gif_img']}
                    src={gif.images.fixed_width_downsampled.url}
                    alt={'gif'}
                  />
                </div>
              );
            })}
            {fetching && <LoadingIndicator view="llama" />}
          </ScrollableContent>
        )}
      </div>
      {failed && !(gifs || []).length ? (
        <div className={s['error_msg']}> {t('ext.gif.search.error')}</div>
      ) : null}
    </DSModal>
  );
};
GiphyModal.displayName = 'GiphyModal';

export default GiphyModal;
